import React, { useLayoutEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '../../../components/ui/card';
import { cn } from '../../../lib/utils';
import { t } from '../i18n/walkthroughI18n';
import { Z_CLASS } from '../../../lib/z-index';
import {
  computeTooltipPosition,
  resolvePreferredPlacement,
} from './walkthroughPosition';

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {string} [props.body]
 * @param {number} props.stepIndex
 * @param {number} props.stepCount
 * @param {boolean} props.isFirst
 * @param {boolean} props.isLast
 * @param {boolean} props.isMobile
 * @param {boolean} props.isModal
 * @param {boolean} [props.isChapterBreak]
 * @param {boolean} [props.isOptionalChapter]
 * @param {DOMRect | null} props.targetRect
 * @param {string | undefined} props.placement
 * @param {() => void} props.onBack
 * @param {() => void} props.onNext
 * @param {() => void} props.onSkip
 * @param {() => void} [props.onSkipChapter]
 * @param {React.RefObject<HTMLDivElement>} props.containerRef
 */
export function WalkthroughTooltip({
  title,
  body,
  stepIndex,
  stepCount,
  isFirst,
  isLast,
  isMobile,
  isModal,
  isChapterBreak = false,
  isOptionalChapter = false,
  targetRect,
  placement,
  onBack,
  onNext,
  onSkip,
  onSkipChapter,
  containerRef,
}) {
  const cardRef = useRef(null);
  const [desktopPosition, setDesktopPosition] = useState(null);

  useLayoutEffect(() => {
    if (isMobile || isModal || !targetRect || !cardRef.current) {
      setDesktopPosition(null);
      return;
    }

    const tooltipRect = cardRef.current.getBoundingClientRect();
    const preferred = resolvePreferredPlacement(placement);
    setDesktopPosition(
      computeTooltipPosition(targetRect, tooltipRect, preferred)
    );
  }, [isMobile, isModal, targetRect, placement, title, body, stepIndex]);

  const stepLabel = t('walkthrough.ui.stepOf', {
    current: stepIndex + 1,
    total: stepCount,
  });

  const actionRow = (
    <div
      className={cn(
        'flex items-center gap-2',
        isMobile ? 'touch-spacing w-full' : 'justify-end'
      )}
    >
      <Button
        type="button"
        variant="ghost"
        onClick={onSkip}
        className={cn(isMobile && 'touch-target flex-1')}
      >
        {t('walkthrough.ui.skipTour')}
      </Button>
      {isChapterBreak && isOptionalChapter ? (
        <Button
          type="button"
          variant="outline"
          onClick={onSkipChapter}
          className={cn(isMobile && 'touch-target flex-1')}
        >
          {t('walkthrough.ui.skipChapter')}
        </Button>
      ) : !isFirst ? (
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className={cn(isMobile && 'touch-target flex-1')}
        >
          {t('walkthrough.ui.back')}
        </Button>
      ) : null}
      <Button
        type="button"
        onClick={onNext}
        className={cn(
          'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700',
          isMobile && 'touch-target flex-1'
        )}
      >
        {isChapterBreak
          ? t('walkthrough.ui.continue')
          : isLast
            ? t('walkthrough.ui.finish')
            : t('walkthrough.ui.next')}
      </Button>
    </div>
  );

  const card = (
    <Card
      ref={cardRef}
      className={cn(
        'border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800',
        isMobile
          ? 'max-h-[45vh] w-full overflow-y-auto rounded-t-xl rounded-b-none border-b-0'
          : 'w-[min(100vw-2rem,22rem)]'
      )}
    >
      <CardHeader className="space-y-2 pb-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {stepLabel}
        </p>
        <DialogPrimitive.Title className="text-base font-semibold leading-none tracking-tight dark:text-gray-100">
          {title}
        </DialogPrimitive.Title>
        {body ? (
          <DialogPrimitive.Description className="text-sm text-muted-foreground dark:text-gray-300">
            {body}
          </DialogPrimitive.Description>
        ) : null}
      </CardHeader>
      {isMobile ? (
        <CardContent className="pt-0">{actionRow}</CardContent>
      ) : (
        <CardFooter className="flex-col items-stretch gap-0 pt-0 sm:flex-row sm:justify-end">
          {actionRow}
        </CardFooter>
      )}
    </Card>
  );

  const preventTourDismiss = (event) => {
    event.preventDefault();
  };

  const contentProps = {
    ref: containerRef,
    onPointerDownOutside: preventTourDismiss,
    onInteractOutside: preventTourDismiss,
    className: cn(
      Z_CLASS.WALKTHROUGH,
      'pointer-events-auto fixed outline-none focus:outline-none'
    ),
  };

  if (isMobile) {
    return (
      <DialogPrimitive.Content
        {...contentProps}
        className={cn(
          contentProps.className,
          'inset-x-0 bottom-0 safe-area-left safe-area-right safe-area-bottom drawer-safe'
        )}
      >
        {card}
      </DialogPrimitive.Content>
    );
  }

  if (isModal || !targetRect) {
    return (
      <DialogPrimitive.Content
        {...contentProps}
        className={cn(
          contentProps.className,
          'left-1/2 top-1/2 w-[min(100vw-2rem,24rem)] -translate-x-1/2 -translate-y-1/2'
        )}
      >
        {card}
      </DialogPrimitive.Content>
    );
  }

  return (
    <DialogPrimitive.Content
      {...contentProps}
      style={
        desktopPosition
          ? {
              top: desktopPosition.top,
              left: desktopPosition.left,
            }
          : { top: -9999, left: -9999, visibility: 'hidden' }
      }
    >
      {card}
    </DialogPrimitive.Content>
  );
}

export default WalkthroughTooltip;
